import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import '../styles/pages/services.css';

const SERVICE_CARDS = [
  {
    id: 'seo',
    icon: 'fa-solid fa-magnifying-glass-chart',
    title: 'SEO',
    text: 'Get found by the customers already searching for what you do, in your area.',
    items: ['Technical audit and fixes', 'Keyword research and on-page optimization', 'Google Business Profile', 'Monthly content and reporting'],
  },
  {
    id: 'web-design',
    icon: 'fa-solid fa-laptop-code',
    title: 'Website design & redesign',
    text: 'A fast, mobile-first website that explains what you do and turns visits into enquiries.',
    items: ['Discovery and sitemap', 'Design with set revision rounds', 'Build, forms and analytics', 'Launch plus 30 days of fixes'],
  },
  {
    id: 'digital-marketing',
    icon: 'fa-solid fa-bullhorn',
    title: 'Digital marketing',
    text: 'Paid ads, social and email campaigns managed against a budget you control.',
    items: ['Google and Meta ads', 'Email campaigns, CASL-compliant', 'Ad copy and creative', 'Monthly performance report'],
  },
];

const STEPS = [
  { title: 'Free call', text: 'We learn what you sell, who buys it, and what you want more of.' },
  { title: 'Fixed quote', text: 'You get a one-page quote with scope, timeline and price, linked to the terms for that service.' },
  { title: 'Accept and start', text: 'Sign the quote, reply to accept, or pay the deposit. No long contract to negotiate.' },
  { title: 'Deliver and report', text: 'We do the work, you approve each stage, and you get a plain report every month.' },
];

const PROMISES = [
  'Your domain, website and ad accounts stay in your name.',
  'Ad spend goes straight from you to Google or Meta, never through us.',
  'You own the finished work once it is paid for.',
  'No ranking or results promises, because nobody honest can make them.',
];

function GrowthServices() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'SEO, Website Design & Digital Marketing — Moneli Automation Inc.';
  }, []);

  return (
    <div className="services-page">
      <PageHero
        eyebrow="Web & marketing"
        title={<>Websites and marketing that <em>bring in work</em></>}
        lead="SEO, website design or redesign, and digital marketing for small and mid-sized businesses. Clear quotes, plain terms, monthly reports."
        crumbs={[{ to: '/', label: 'Home' }, { to: '/services', label: 'Services' }, { label: 'Web & marketing' }]}
        actions={
          <>
            <Link to="/contact" className="btn btn-primary btn-lg" data-magnetic>Get a quote</Link>
            <Link to="/terms" className="btn btn-ghost btn-lg">Read our terms</Link>
          </>
        }
      />

      <section className="section section--light" aria-labelledby="gs-services-title">
        <div className="container">
          <SectionHeader
            index="01"
            eyebrow="Services"
            title={<>Three ways we <em>help</em></>}
            lead="Pick one or combine them. Each has its own short terms page, linked from your quote."
            id="gs-services-title"
          />
          <div className="grid-3 svc-grid" data-reveal-stagger="0.1">
            {SERVICE_CARDS.map((s, i) => (
              <article key={s.id} className="card card--feature card--num svc-card" data-num={String(i + 1).padStart(2, '0')}>
                <span className="icon-tile"><i className={s.icon} aria-hidden="true" /></span>
                <h3 className="card__title">{s.title}</h3>
                <p className="card__text">{s.text}</p>
                <ul className="ticks svc-card__ticks">
                  {s.items.map((it) => <li key={it}>{it}</li>)}
                </ul>
                <Link to={`/terms/${s.id}`} className="btn btn-link">
                  {s.title} terms <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--sand" aria-labelledby="gs-steps-title">
        <div className="container">
          <SectionHeader
            index="02"
            eyebrow="How it works"
            title={<>From call to <em>results</em></>}
            lead="A short quote and a public terms page replace the long contract."
            id="gs-steps-title"
          />
          <ol className="steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <span className="steps__num">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="steps__title">{s.title}</h3>
                <p className="steps__text">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section section--light" aria-labelledby="gs-terms-title">
        <div className="container">
          <SectionHeader
            index="03"
            eyebrow="Plain terms"
            title={<>What you can <em>count on</em></>}
            lead="The full terms are public, so you can read them before you call."
            id="gs-terms-title"
          />
          <ul className="ticks">
            {PROMISES.map((p) => <li key={p}>{p}</li>)}
          </ul>
          <p>
            <Link to="/terms/seo">SEO terms</Link> · <Link to="/terms/web-design">Website terms</Link> ·{' '}
            <Link to="/terms/digital-marketing">Digital marketing terms</Link>
          </p>
        </div>
      </section>
    </div>
  );
}

export default GrowthServices;
